import React from 'react';

import { flexRender } from '@tanstack/react-table';
import type { RowData } from '@tanstack/react-table';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import type { TableViewBodyProps } from './types';
import { buildTestid } from '../../../lib/utils';
import './TableViewBody.sass';

const displayName = 'TableViewBody';
const testid = buildTestid(displayName);

function TableViewBody<TData extends RowData>({
  table,
  className = undefined,
  'data-testid': dataTestId = undefined,
}: TableViewBodyProps<TData>): React.JSX.Element {
  const { t } = useTranslation();
  const { rows } = table.getRowModel();

  return (
    <div
      className={classNames('TableView__Body', className)}
      {...testid(dataTestId)}
    >
      {rows.map((row) => (
        <div
          className={classNames('TableView__Row', {
            TableView__Row_isSelected: row.getIsSelected(),
          })}
          {...testid(`TableViewRow_${row.id}`, dataTestId)}
          key={row.id}
        >
          {row.getVisibleCells().map((cell) => (
            <React.Fragment key={cell.id}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </React.Fragment>
          ))}
        </div>
      ))}

      {!rows.length && (
        <div className="TableView__Row TableView__Row_empty">
          <div className="TableView__BodyCell_empty">
            {t('uikit:table.empty_message')}
          </div>
        </div>
      ) }
    </div>
  );
}

TableViewBody.displayName = displayName;

export { TableViewBody };
